import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { ITableControlMap } from "@store/filters/filter.types";
import hashStorage from "@store/filters/hashStorage";
import { INITIAL_FILTERS_STATES_MAP } from "@store/filters/initialStates";

interface ITablesControlsToReset {
  filters?: boolean;
  pagination?: boolean;
  selection?: boolean;
  sort?: boolean;
}

export type TableControlKeyType = keyof ITableControlMap;

export type TableControlGroupType = ITableControlMap[TableControlKeyType];

interface IFilterStore extends ITableControlMap {
  resetTableStore: (key: TableControlKeyType, statesToReset?: ITablesControlsToReset) => void;
  setTableFilters: (key: TableControlKeyType, filters: TableControlGroupType["filters"]) => void;
  setTablePagination: (key: TableControlKeyType, pagination: TableControlGroupType["pagination"]) => void;
  setTableSelection: (key: TableControlKeyType, selection: TableControlGroupType["selection"]) => void;
  setTableSort: (key: TableControlKeyType, sort: TableControlGroupType["sort"]) => void;
}

const TableControlsStore = create<IFilterStore>()(
  persist(
    (set) => ({
      company: INITIAL_FILTERS_STATES_MAP["company"],
      contact: INITIAL_FILTERS_STATES_MAP["contact"],
      resetTableStore: (key, controlsToReset) => {
        if (!controlsToReset || Object.keys(controlsToReset).length === 0) {
          set(() => ({
            [key]: INITIAL_FILTERS_STATES_MAP[key],
          }));
          return;
        }
        set((previous) => ({
          ...previous,
          [key]: {
            filters: controlsToReset.filters ? INITIAL_FILTERS_STATES_MAP[key]["filters"] : previous[key]["filters"],
            pagination: controlsToReset.pagination
              ? INITIAL_FILTERS_STATES_MAP[key]["pagination"]
              : previous[key]["pagination"],
            selection: controlsToReset.selection
              ? INITIAL_FILTERS_STATES_MAP[key]["selection"]
              : previous[key]["selection"],
            sort: controlsToReset.sort ? INITIAL_FILTERS_STATES_MAP[key]["sort"] : previous[key]["sort"],
          },
        }));
      },
      setTableFilters: (key, filters) => {
        set((previous) => ({
          [key]: {
            ...previous[key],
            filters: {
              ...previous[key].filters,
              ...filters,
            },
          },
        }));
      },
      setTablePagination: (key, pagination) => {
        set((previous) => ({
          [key]: {
            ...previous[key],
            pagination,
          },
        }));
      },
      setTableSelection: (key, selection) => {
        set((previous) => ({
          [key]: {
            ...previous[key],
            selection,
          },
        }));
      },
      setTableSort: (key, sort) => {
        set((previous) => ({
          [key]: {
            ...previous[key],
            sort,
          },
        }));
      },
      technologyCompany: INITIAL_FILTERS_STATES_MAP["technologyCompany"],
      technologyContact: INITIAL_FILTERS_STATES_MAP["technologyContact"],
    }),
    {
      name: "filters",
      storage: createJSONStorage(() => hashStorage),
    },
  ),
);

type FilterStoreReturnType<T extends TableControlKeyType> = {
  resetTableStore: (statesToReset?: ITablesControlsToReset) => void;
  setTableFilters: (filtersToUpdate: TableControlGroupType["filters"]) => void;
  setTablePagination: (newPagination: TableControlGroupType["pagination"]) => void;
  setTableSelection: (newSelection: TableControlGroupType["selection"]) => void;
  setTableSort: (newSort: TableControlGroupType["sort"]) => void;
} & IFilterStore[T];

const useTableStore = <T extends TableControlKeyType>(key: T): FilterStoreReturnType<T> => {
  const state = TableControlsStore.getState();

  const { filters, pagination, selection, sort } = state[key];

  return {
    filters,
    pagination,
    resetTableStore: (statesToReset?: ITablesControlsToReset) => state.resetTableStore(key, statesToReset),
    selection,
    setTableFilters: (filtersToUpdate: TableControlGroupType["filters"]) => state.setTableFilters(key, filtersToUpdate),
    setTablePagination: (newPagination: TableControlGroupType["pagination"]) =>
      state.setTablePagination(key, newPagination),
    setTableSelection: (newSelection: TableControlGroupType["selection"]) => state.setTableSelection(key, newSelection),
    setTableSort: (newSort: TableControlGroupType["sort"]) => state.setTableSort(key, newSort),
    sort,
  };
};

export { useTableStore };
